import type { AnyChannelContract } from 'shared/channels/contract';
import type { PullStepReport } from 'shared/domain/messages/serviceWorkerHealth';

/** Where the last pull of an entity got to; a pull that stops between steps leaves its last one. */
export enum PullStep {
  /** A read asked for the first download. */
  requested = 'requested',
  /** The request was dropped: operations stayed suspended (logout, project flush). */
  skippedWhileSuspended = 'skipped-while-suspended',
  readingConfig = 'reading-config',
  /** No token or config: retried after the auth cooldown or the next applied config. */
  configUnavailable = 'config-unavailable',
  /** The config lacks keys the pull needs: retried after the next applied config. */
  waitingForConfig = 'waiting-for-config',
  /** The access to the project is not known yet: retried once the permissions are stored. */
  waitingForPermissions = 'waiting-for-permissions',
  readingState = 'reading-state',
  /** The data became outdated (logout, project switch) while the pull was decided. */
  scopeOutdated = 'scope-outdated',
  /** Nothing to download (downloaded, or marked as downloading). */
  notNeeded = 'not-needed',
  /** The user has no access to the entity (`canSynchronize`): nothing is downloaded, nothing will be. */
  notAllowed = 'not-allowed',
  /** Another pull of the entity runs in this worker. */
  alreadyRunning = 'already-running',
  started = 'started',
  /** The fetch was not made (no API address, or the pull was aborted). */
  fetchSkipped = 'fetch-skipped',
  ended = 'ended',
  failed = 'failed',
  /** The pull or sync was aborted after it started (logout, project switch, worker update): nothing to report. */
  aborted = 'aborted',
  /** The API no longer knows the sync key: the collection was cleared and the next read downloads it in full. */
  syncKeyRefused = 'sync-key-refused',
  /**
   * The last pull of the entity in this project failed a moment ago: this one was
   * not started (nothing cleared, nothing asked), its failure was told again.
   */
  failedRecently = 'failed-recently',
}

/** By the contract key of the entity, in memory: what this worker session did. */
const lastSteps = new Map<string, { step: PullStep; at: number }>();

type EntityKey = Pick<AnyChannelContract, 'key'>;

export function markPullStep({ key }: EntityKey, step: PullStep): void {
  lastSteps.set(key, { step, at: Date.now() });
}

/** `markPullStep` bound to one entity, for code that marks many steps of its pull. */
export function pullStepMarker(entity: EntityKey): (step: PullStep) => void {
  return (step) => markPullStep(entity, step);
}

function toReport({ step, at }: { step: PullStep; at: number }): PullStepReport {
  return { step, ageMs: Date.now() - at };
}

/** The last step of the entity's pull; `undefined` before any. */
export function readPullStep({ key }: EntityKey): PullStepReport | undefined {
  const progress = lastSteps.get(key);
  return progress && toReport(progress);
}

/** Whether the entity's pull is parked until the worker has its config: it goes on by itself once it does. */
export function isPullParkedForConfig(entity: EntityKey): boolean {
  const step = readPullStep(entity)?.step;
  return step === PullStep.waitingForConfig || step === PullStep.configUnavailable;
}

/** Steps of a pull that goes on to start: asked for, deciding, or parked until the config comes. */
const STEPS_BEFORE_START: ReadonlySet<PullStep> = new Set([
  PullStep.requested,
  PullStep.readingConfig,
  PullStep.readingState,
  PullStep.configUnavailable,
  PullStep.waitingForConfig,
]);

/**
 * Whether a pull of the entity is on its way but not running yet (e.g. held
 * back by a project switch): a collection published meanwhile is not its end.
 */
export function isPullAboutToStart({ key }: EntityKey): boolean {
  const progress = lastSteps.get(key);
  return progress !== undefined && STEPS_BEFORE_START.has(progress.step);
}

/** Whether the entity's last pull was refused for the user's access: a read has nothing to wait for. */
export function isPullNotAllowed(entity: EntityKey): boolean {
  return readPullStep(entity)?.step === PullStep.notAllowed;
}

/** The last step of every entity pulled in this worker session, for diagnostics. */
export function readPullSteps(): Record<string, PullStepReport> {
  return Object.fromEntries([...lastSteps].map(([key, progress]) => [key, toReport(progress)]));
}

/** test-only */
export function clearPullStepsForTests(): void {
  lastSteps.clear();
}
